import { createRouter, createWebHistory } from 'vue-router'
import { useAuthStore } from '@general/auth/frontend'
import { isDemoInstance } from '@general/demo/frontend'

import { allows, passwordChangeDetour } from './access.js'
import { moduleRoutes } from './registry'
import { routeRequirementsMet } from './navigation'

const routes = [
  {
    path: '/',
    name: 'home',
    component: () => import('../views/HomeView.vue'),
    meta: { title: 'home.title' },
  },
  {
    path: '/ops',
    name: 'ops',
    component: () => import('../views/OpsView.vue'),
    // Instance operations belong to whoever runs the instance, not to the client.
    meta: { permission: 'ops:read', title: 'ops.title' },
  },
  // ── La documentación ───────────────────────────────────────────────────────
  //
  // Su propia sección y su propio armazón: SIN el menú del ERP. Quien está
  // aprendiendo tiene esto abierto en una pestaña y el ERP en la de al lado, y
  // dos menús idénticos en las dos pestañas es la manera de perderse entre
  // ellas. Aquí el índice es el de la documentación.
  //
  // Antes de `moduleRoutes` porque `/help` es de la plataforma y ningún módulo
  // puede quedárselo, la misma regla que /login o /users.
  {
    path: '/help',
    name: 'help',
    component: () => import('../views/HelpIndexView.vue'),
    meta: { title: 'help.title', layout: 'docs' },
  },
  {
    path: '/help/:topic',
    name: 'help-topic',
    component: () => import('../views/HelpTopicView.vue'),
    meta: { title: 'help.title', layout: 'docs' },
  },
  ...moduleRoutes,
  {
    path: '/:pathMatch(.*)*',
    name: 'not-found',
    component: () => import('../views/NotFoundView.vue'),
    meta: { public: true, layout: 'auth' },
  },
]

const router = createRouter({
  // El prefijo donde vive la aplicacion, que en la instalacion normal es la
  // raiz y en una instancia servida bajo subruta no lo es. Sale del `base`
  // de vite (APP_BASE en la construccion de la imagen), asi que el enrutador
  // y los assets no pueden discrepar: los dos leen el mismo numero.
  history: createWebHistory(import.meta.env.BASE_URL),
  routes,
  scrollBehavior: () => ({ top: 0 }),
})

router.beforeEach(async (to) => {
  const auth = useAuthStore()

  // ── Una pantalla que necesita otro módulo (`meta.requires`) ────────────────
  //
  // `meta: { requires: ['construction'] }` —uno o varios, y hacen falta TODOS—
  // es la misma regla que `requires` en la entrada del menú (navigationRules.js)
  // y se contesta con la misma función: lo que no se ve en el menú tampoco se
  // abre escribiendo la dirección.
  //
  // Acaba en «no encontrado» y no en el inicio, a diferencia del permiso: no es
  // que esta persona no pueda verla, es que en esta instancia la pantalla no
  // existe, y eso es lo que tiene que leer quien llega con un enlace guardado.
  // La dirección se conserva para que se vea qué se pidió.
  //
  // Antes que la sesión: que una pantalla exista no depende de quién la pida.
  // `meta` llega fundido desde las rutas padre, así que un `requires` en la
  // ruta de arriba vale para todas sus hijas.
  if (to.meta.requires && !(await routeRequirementsMet(to.meta.requires))) {
    return {
      name: 'not-found',
      params: { pathMatch: to.path.split('/').filter(Boolean) },
      query: to.query,
      hash: to.hash,
    }
  }

  if (to.meta.public) return true

  if (!auth.isAuthenticated) {
    // On a demo instance there IS no sign-in screen: a visitor gets a session
    // by opening a URL that names modules, and the selector is where they say
    // which. Sending them to a password prompt they can never satisfy is the
    // one way to lose somebody in the first three seconds.
    if (isDemoInstance()) return { name: 'demo-selector' }

    // Remember where they were headed so the login can send them back.
    return { name: 'login', query: { redirect: to.fullPath } }
  }

  // Una contraseña que conoce otro: el servidor no da ningún permiso con ella
  // (plataforma 0.24.0), así que al perfil, donde se cambia, también con la
  // sesión abierta de antes (access.js lo explica).
  const detour = passwordChangeDetour(auth.user, to)
  if (detour) return detour

  // Permission gate. The backend enforces this too — this only avoids showing a
  // screen that would answer 403 on every request.
  //
  // A route may name several: the same screen reached by two different rights
  // (see access.js). ANY of them opens it, exactly as the backend decides.
  if (!allows(to.meta.permission, auth.can)) {
    return { name: 'home' }
  }

  return true
})

// After a deployment the old lazy chunks are gone. A hard navigation fetches the
// new index.html instead of leaving the user on a blank screen.
router.onError((error, to) => {
  const isChunkError = /Failed to fetch dynamically imported module|Importing a module script failed|Unable to preload/i
    .test(error.message ?? '')
  if (isChunkError) window.location.assign(to.fullPath)
})

export default router
